import React, { FC, useState, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faQuestion } from '@fortawesome/pro-light-svg-icons';
import UserButton from '../UserButton';
import UserName from 'src/common/UserName';
import Input from 'src/common/Input';
import Skeleton from 'src/common/Skeleton';
import classNames from 'classnames';
import { IUserName } from 'src/models/users/types';
import styles from './index.module.scss';
import dropDownOverlayStyles from 'src/shared/DropDownWithSearch/index.module.scss';

export interface UserPickerOverlayProps {
  users: IUserName[];
  usersLoading?: boolean;
  selected?: IUserName | null;
  children?: ReactNode;
  onSelect: (email: string) => void;
  onUnSelect?: () => void;
}

export const UserPickerOverlay: FC<UserPickerOverlayProps> = ({
  selected,
  onSelect,
  onUnSelect,
  users,
  usersLoading,
  children
}) => {
  const { t } = useTranslation();

  const notAssignedUsers = useMemo(() => {
    return (selected && users.filter(user => user.email !== selected.email)) || users;
  }, [selected, users]);
  const [searchPhrase, setSearchPhrase] = useState('');

  const filteredUsers = useMemo(() => {
    const searchString = searchPhrase.toLowerCase();
    return (
      (!!notAssignedUsers &&
        notAssignedUsers.filter(
          user =>
            (user.name && user.name.toLowerCase().includes(searchString)) ||
            (user.lastName && user.lastName.toLowerCase().includes(searchString)) ||
            user.email.toLowerCase().includes(searchString) ||
            user.userName.toLowerCase().includes(searchString)
        )) ||
      notAssignedUsers
    );
  }, [notAssignedUsers, searchPhrase]);

  return (
    <div className={dropDownOverlayStyles.menu}>
      <div className={dropDownOverlayStyles.menuInner}>
        {selected && !!onUnSelect && (
          <div className={styles.currentUser}>
            <UserName user={selected} className={styles.currentUserName} size={'s'} />
            <button className={classNames(styles.button, styles.unAssignButton)} onClick={onUnSelect}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
        <div className={dropDownOverlayStyles.search}>
          <Input
            bordered={false}
            placeholder={t('Teammates.searchTeammates')}
            className={dropDownOverlayStyles.searchField}
            name={'searchPhrase'}
            onChange={e => setSearchPhrase(e.target.value)}
            value={searchPhrase}
            prefix={<FontAwesomeIcon icon={faSearch} />}
            suffix={
              !!searchPhrase && (
                <button onClick={() => setSearchPhrase('')} className={dropDownOverlayStyles.buttonClearSearch}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )
            }
          />
        </div>
        <div className={dropDownOverlayStyles.container}>
          {usersLoading ? (
            <Skeleton loading active />
          ) : !!filteredUsers.length ? (
            filteredUsers.map(user => <UserButton key={user.email} user={user} onClick={onSelect} />)
          ) : (
            <div className={dropDownOverlayStyles.searchEmpty}>
              <span className={dropDownOverlayStyles.searchEmptyPic}>
                <FontAwesomeIcon icon={faQuestion} className={dropDownOverlayStyles.searchEmptyIcon} />
              </span>
              <span className={dropDownOverlayStyles.searchEmptyLabel}>{t('Common.noMatchesFound')}</span>
            </div>
          )}
        </div>
        {!!children && children}
      </div>
    </div>
  );
};

export default UserPickerOverlay;
